import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import { CSVLink } from 'react-csv';
import Select from 'react-select';
import moment from 'moment';
import Container from '../../shared/Container';
import Section from '../../shared/Section';
import colors from '../../styles/colors';
import NoContent from '../../shared/NoContent';
import Button from '../Button';
import Icon from '../Icon';
import { IconEnum as Icons } from '../Icons';
import BaseModal, { ModalList } from '../modals/BaseModal';
import {
  GET_SUPERADMIN_CAMPAIGNS,
  GET_SUPERADMIN_FILTERS_DATA,
} from '../../graphql/queries';

const STATUS_OPTIONS = ['Draft', 'Scheduled', 'Sent', 'Failed', 'Deleted'].map((value) => ({
  value,
  label: value,
}));

const DEFAULT_STATUS_VALUES = ['Scheduled', 'Sent', 'Failed'];
const ALL_ORGANIZATIONS_OPTION = {
  value: '',
  label: 'All organizations',
};

const defaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  date.setHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 10);
};

const toUnixSeconds = (value, endOfDay = false) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (endOfDay) date.setHours(23, 59, 59, 999);
  return Math.floor(date.getTime() / 1000);
};

const normalizeComparable = (campaign, key) => {
  if (key === 'tags') return (campaign.tags || []).join(', ');
  if (key === 'dateTime') return Number(campaign.dateTime || 0);
  if (['numberSent', 'totalUrlClicks', 'creditsUsed', 'optOuts'].includes(key)) return Number(campaign[key] || 0);
  return (campaign[key] || '').toString().toLowerCase();
};

const displayStatus = (campaign) => {
  if (campaign.status === 'Sent' && ['Pending', 'Processing'].includes(campaign.executionStatus)) {
    return 'In Progress';
  }
  return campaign.status;
};

const csvHeaders = [
  { label: 'ORGANIZATION NAME', key: 'organizationName' },
  { label: 'CAMPAIGN NAME', key: 'name' },
  { label: 'STATUS', key: 'status' },
  { label: 'DATE-TIME', key: 'dateTime' },
  { label: 'MESSAGE', key: 'text' },
  { label: 'TAGS', key: 'tags' },
  { label: 'NUMBER SENT', key: 'numberSent' },
  { label: 'CLICK-THRUS / CONVERSIONS', key: 'totalUrlClicks' },
  { label: 'CREDIT USED', key: 'creditsUsed' },
  { label: 'OPT-OUTs', key: 'optOuts' },
];

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 10px;
`;

const FilterItem = styled.div`
  border: 1px solid ${colors.grey5};
  min-height: 63px;
  padding: 4px 6px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  box-sizing: border-box;
  background: ${colors.white};
`;

const DateFilterItem = styled(FilterItem)`
  width: 150px;
`;

const OrganizationFilterItem = styled(FilterItem)`
  width: 200px;
`;

const StatusFilterItem = styled(FilterItem)`
  width: 360px;
`;

const ApplyButtonContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  padding-top: 6px;
`;

const Label = styled.label`
  font-size: 1.2rem;
  color: ${colors.grey3};
  display: block;
  margin-bottom: 4px;
`;

const DateInput = styled.input`
  width: 100%;
  height: 25px;
  border: 1px solid ${colors.grey5};
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  color: ${colors.black};
`;

const TableContainer = styled.div`
  background: ${colors.white};
  width: 100%;
  max-height: 60vh;
  overflow: auto;
  border: 1px solid ${colors.grey6};
  border-radius: 0 10px 10px 0;
`;

/* One logical table so column widths match header + all body rows (not separate display:table per row). */
const TableGrid = styled.div`
  display: table;
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.div`
  display: table-header-group;
`;

const TableBody = styled.div`
  display: table-row-group;
`;

const TableRow = styled.div`
  display: table-row;
  &:hover { background: rgba(211, 211, 211, 0.1); }
`;

const NameCellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const Cell = styled.div`
  display: table-cell;
  min-width: ${(props) => props.width || '150px'};
  width: ${(props) => props.width || '150px'};
  max-width: ${(props) => props.width || '150px'};
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;
  border-bottom: 1px solid ${colors.grey5};
  border-right: 1px solid ${colors.grey5};
  padding: 10px 12px;
  font-size: 13px;
  color: ${colors.grey3};
  cursor: ${(props) => (props.header ? 'pointer' : 'default')};
  ${(props) => (props.header ? `
    position: sticky;
    top: 0;
    background: ${colors.white};
    z-index: 5;
    box-shadow: inset 0 -1px 0 ${colors.grey5};
  ` : '')}
`;

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 25,
    height: 25,
    borderColor: colors.grey5,
    boxShadow: 'none',
    borderRadius: 4,
    fontSize: 13,
    backgroundColor: colors.white,
    '&:hover': {
      borderColor: colors.grey5,
    },
  }),
  valueContainer: (base) => ({
    ...base,
    minHeight: 25,
    height: 25,
    padding: '0 6px',
    overflow: 'hidden',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    minHeight: 25,
    height: 25,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: 13,
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 13,
    color: colors.black,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 13,
    color: colors.black,
  }),
  multiValue: (base) => ({
    ...base,
    fontSize: 13,
    minHeight: 18,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    margin: '0 4px 0 0',
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: 13,
    padding: '0',
    color: colors.black,
  }),
  multiValueRemove: (base) => ({
    ...base,
    display: 'none',
  }),
  menu: (base) => ({
    ...base,
    fontSize: 13,
    zIndex: 20,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    backgroundColor: state.isSelected ? colors.white : (state.isFocused ? colors.grey6 : colors.white),
    color: colors.black,
    ':active': {
      backgroundColor: colors.grey6,
    },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    color: colors.grey3,
    padding: '2px 6px',
    ':hover': {
      color: colors.grey3,
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: colors.grey3,
    padding: '2px 6px',
    ':hover': {
      color: colors.grey3,
    },
  }),
};

export default function SuperadminCampaigns() {
  const csvRef = useRef();
  const [draftStartDate, setDraftStartDate] = useState(defaultStartDate());
  const [draftEndDate, setDraftEndDate] = useState('');
  const [draftSelectedOrganization, setDraftSelectedOrganization] = useState(ALL_ORGANIZATIONS_OPTION);
  const [draftSelectedStatuses, setDraftSelectedStatuses] = useState(
    STATUS_OPTIONS.filter((item) => DEFAULT_STATUS_VALUES.includes(item.value)),
  );
  const [appliedStartDate, setAppliedStartDate] = useState(defaultStartDate());
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [appliedSelectedOrganization, setAppliedSelectedOrganization] = useState(ALL_ORGANIZATIONS_OPTION);
  const [appliedSelectedStatuses, setAppliedSelectedStatuses] = useState(
    STATUS_OPTIONS.filter((item) => DEFAULT_STATUS_VALUES.includes(item.value)),
  );
  const [ordering, setOrdering] = useState({ fieldName: 'dateTime', order: 'desc' });
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [campaignId, setCampaignId] = useState('');

  const { data: filterData } = useQuery(GET_SUPERADMIN_FILTERS_DATA);
  const organizationOptions = [
    ALL_ORGANIZATIONS_OPTION,
    ...(filterData?.getSuperAdminFiltersData?.organizations || []).map((org) => ({
      value: org._id,
      label: org.name,
    })),
  ];

  const query = useMemo(() => {
    const nextQuery = {
      statuses: appliedSelectedStatuses.map((item) => item.value),
    };
    if (appliedSelectedOrganization?.value) {
      nextQuery.organizationIds = [appliedSelectedOrganization.value];
    }

    const parsedStartDate = toUnixSeconds(appliedStartDate);
    const parsedEndDate = toUnixSeconds(appliedEndDate, true);
    if (Number.isInteger(parsedStartDate)) {
      nextQuery.startDate = parsedStartDate;
    }
    if (Number.isInteger(parsedEndDate)) {
      nextQuery.endDate = parsedEndDate;
    }

    return nextQuery;
  }, [appliedStartDate, appliedEndDate, appliedSelectedOrganization, appliedSelectedStatuses]);

  const applyFilters = () => {
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    setAppliedSelectedOrganization(draftSelectedOrganization);
    setAppliedSelectedStatuses(draftSelectedStatuses);
  };

  const { data, loading } = useQuery(GET_SUPERADMIN_CAMPAIGNS, {
    variables: { query },
    fetchPolicy: 'network-only',
  });

  const campaigns = data?.getSuperAdminCampaigns || [];
  const sortedCampaigns = useMemo(() => {
    const copied = [...campaigns];
    const sortOrder = ordering.order === 'asc' ? 1 : -1;
    copied.sort((a, b) => {
      const left = normalizeComparable(a, ordering.fieldName);
      const right = normalizeComparable(b, ordering.fieldName);
      if (left < right) return -1 * sortOrder;
      if (left > right) return 1 * sortOrder;
      return 0;
    });
    return copied;
  }, [campaigns, ordering]);

  const exportData = sortedCampaigns.map((campaign) => ({
    organizationName: campaign.organizationName || '',
    name: campaign.name || '',
    status: displayStatus(campaign),
    dateTime: campaign.status === 'Draft' ? 'Drafted' : moment(Number(campaign.dateTime || 0) * 1000).format('MM/DD/YYYY hh:mm A'),
    text: campaign.text || '',
    tags: Array.isArray(campaign.tags) ? campaign.tags.join(', ') : '',
    numberSent: campaign.numberSent || 0,
    totalUrlClicks: campaign.totalUrlClicks || 0,
    creditsUsed: campaign.creditsUsed || 0,
    optOuts: campaign.optOuts || 0,
  }));

  const toggleSort = (fieldName) => {
    setOrdering((old) => ({
      fieldName,
      order: old.fieldName === fieldName && old.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toolbarItems = exportData.length > 0
    ? [{
      icon: Icons.FileExcel,
      text: 'Download CSV',
      onClick: () => csvRef?.current?.link?.click?.(),
    }]
    : [];

  const sortIcon = (fieldName) => {
    if (ordering.fieldName !== fieldName) return Icons.Sort;
    return ordering.order === 'asc' ? Icons.SortUp : Icons.SortDown;
  };

  const viewCampaignDetails = (selectedCampaignId) => {
    setCampaignId(selectedCampaignId);
    setDetailsModalVisible(true);
  };

  return (
    <Container>
      <BaseModal
        open={detailsModalVisible}
        modalType={ModalList.CampaignDetailsModal}
        onClose={setDetailsModalVisible}
        campaignId={campaignId}
      />
      <Section
        headerTitle="Superadmin Campaigns"
        toolbarItems={toolbarItems}
        loading={loading}
        contentDirection="column"
      >
        <CSVLink
          data={exportData}
          headers={csvHeaders}
          filename="superadmin-campaigns.csv"
          ref={csvRef}
          target="_self"
        />

        <Filters>
          <DateFilterItem>
            <Label>Start Date</Label>
            <DateInput type="date" value={draftStartDate} onChange={(e) => setDraftStartDate(e.target.value)} />
          </DateFilterItem>
          <DateFilterItem>
            <Label>End Date</Label>
            <DateInput type="date" value={draftEndDate} onChange={(e) => setDraftEndDate(e.target.value)} />
          </DateFilterItem>
          <OrganizationFilterItem>
            <Label>Organization</Label>
            <Select
              isClearable={false}
              value={draftSelectedOrganization}
              onChange={(value) => setDraftSelectedOrganization(value || ALL_ORGANIZATIONS_OPTION)}
              options={organizationOptions}
              styles={selectStyles}
            />
          </OrganizationFilterItem>
          <StatusFilterItem>
            <Label>Status</Label>
            <Select
              isMulti
              isClearable
              value={draftSelectedStatuses}
              onChange={(value) => setDraftSelectedStatuses(value || [])}
              options={STATUS_OPTIONS}
              placeholder="Select statuses"
              styles={selectStyles}
            />
          </StatusFilterItem>
          <ApplyButtonContainer>
            <Button
              type="button"
              onClick={applyFilters}
              text="Apply"
              padding="0px"
              minWidth="100px"
              minHeight="30px"
              height="30px"
              width="max-content"
            />
          </ApplyButtonContainer>
        </Filters>

        {sortedCampaigns.length < 1 ? (
          <NoContent
            header="No Campaigns Found"
            description="No non-trigger campaigns match the current filters."
          />
        ) : (
          <TableContainer>
            <TableGrid>
              <TableHeader>
                <TableRow>
                  <Cell width="220px" header onClick={() => toggleSort('organizationName')}>ORGANIZATION NAME <Icon icon={sortIcon('organizationName')} size={12} active /></Cell>
                  <Cell width="250px" header onClick={() => toggleSort('name')}>CAMPAIGN NAME <Icon icon={sortIcon('name')} size={12} active /></Cell>
                  <Cell width="170px" header onClick={() => toggleSort('status')}>STATUS <Icon icon={sortIcon('status')} size={12} active /></Cell>
                  <Cell width="170px" header onClick={() => toggleSort('dateTime')}>DATE-TIME <Icon icon={sortIcon('dateTime')} size={12} active /></Cell>
                  <Cell width="400px" header onClick={() => toggleSort('text')}>MESSAGE <Icon icon={sortIcon('text')} size={12} active /></Cell>
                  <Cell width="180px" header onClick={() => toggleSort('tags')}>TAGS <Icon icon={sortIcon('tags')} size={12} active /></Cell>
                  <Cell width="150px" header onClick={() => toggleSort('numberSent')}>NUMBER SENT <Icon icon={sortIcon('numberSent')} size={12} active /></Cell>
                  <Cell width="170px" header onClick={() => toggleSort('totalUrlClicks')}>CLICK-THRUS / CONVERSIONS <Icon icon={sortIcon('totalUrlClicks')} size={12} active /></Cell>
                  <Cell width="140px" header onClick={() => toggleSort('creditsUsed')}>CREDIT USED <Icon icon={sortIcon('creditsUsed')} size={12} active /></Cell>
                  <Cell width="140px" header onClick={() => toggleSort('optOuts')}>OPT-OUTs <Icon icon={sortIcon('optOuts')} size={12} active /></Cell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCampaigns.map((campaign) => (
                  <TableRow key={campaign._id}>
                    <Cell width="220px">{campaign.organizationName || ''}</Cell>
                    <Cell width="250px">
                      <NameCellContent>
                        <span>{campaign.name}</span>
                        <ActionIcons>
                          {campaign.executionStatus === 'Completed' && (
                            <Icon
                              icon={Icons.EyeRegular}
                              onClick={() => viewCampaignDetails(campaign._id)}
                              size={18}
                              active
                            />
                          )}
                        </ActionIcons>
                      </NameCellContent>
                    </Cell>
                    <Cell width="170px">{displayStatus(campaign)}</Cell>
                    <Cell width="170px">{campaign.status === 'Draft' ? 'Drafted' : moment(Number(campaign.dateTime || 0) * 1000).format('MM/DD/YYYY hh:mm A')}</Cell>
                    <Cell width="400px">{campaign.text}</Cell>
                    <Cell width="180px">{Array.isArray(campaign.tags) ? campaign.tags.join(', ') : ''}</Cell>
                    <Cell width="150px">{campaign.numberSent || 0}</Cell>
                    <Cell width="170px">{campaign.totalUrlClicks || 0}</Cell>
                    <Cell width="140px">{campaign.creditsUsed || 0}</Cell>
                    <Cell width="140px">{campaign.optOuts || 0}</Cell>
                  </TableRow>
                ))}
              </TableBody>
            </TableGrid>
          </TableContainer>
        )}
      </Section>
    </Container>
  );
}
